import * as Sentry from '@sentry/browser';
import './PlanVr.scss';
import { HelpControl } from './Controls/HelpControl';
import { ScreenshotControl } from './Controls/ScreenshotControl';
import { TimeControl } from './Controls/TimeControl';
import { FloorControl } from './Controls/FloorControl';
import { FullscreenControl } from './Controls/FullscreenControl';
import { LogoInfo } from './Controls/LogoInfo';
import { MovemodesControl } from './Controls/MovemodesControl';

export const PLAN_VR_SELECTORS = {
  CONTAINER: '#plan-vr',
  IFRAME: '#iframe-vr',
  CONTROLS: '[data-vr="controls"]',
  TRIGGER: '[data-vr="trigger"]',
};

class PlanVr {
  constructor(link, config = {}) {
    this.config = config;
    this.node = document.querySelector(PLAN_VR_SELECTORS.CONTAINER);
    if (!this.node) return;
    this.controlsContainer = this.node.querySelector(PLAN_VR_SELECTORS.CONTROLS);
    this.iframe = this.node.querySelector(PLAN_VR_SELECTORS.IFRAME);
    if (!this.controlsContainer || !this.iframe || !link) return;

    this.init(link);
  }

  init(link) {
    const trigger = this.node.querySelector(PLAN_VR_SELECTORS.TRIGGER);

    trigger.addEventListener('click', () => {
      this.addControls();
      trigger.classList.add('hidden');
    });

    this.iframe.src = link;
  }

  addControls() {
    this.ifwin = this.iframe.contentWindow;
    this.topControlsWrapper = this.controlsContainer.querySelector('.TopControls');
    this.bottomControlsWrapper = this.controlsContainer.querySelector('.BottomControls');
    this.initdone = false;

    window.addEventListener('message', this.onMessage, false);

    // Шлемо повідомлення, доки не отримаємо першу відповідь
    this.timer = setInterval(() => {
      if (this.initdone) {
        clearInterval(this.timer);
        return;
      }

      try {
        this.ifwin?.postMessage({ type: 'buttons', action: 'init' }, '*');
      } catch (error) {
        Sentry.captureException(error);

        clearInterval(this.timer);
      }
    }, 100);
  }

  onMessage = (event) => {
    const data = event.data;

    if (data.type !== 'buttons') return;

    switch (data.action) {
      case 'init':
        this.initAction(data);
        break;
      case 'visibility':
        this.visibilityAction(data);
        break;
      case 'options_enabled':
        // Задання доступності опцій для зміни їх значень кліком
        break;
      case 'option_visible':
        this.optionVisibleAction(data);
        break;
      case 'option':
        this.optionAction(data);
        break;
      case 'logo_info':
        this.logoInfoAction();
        break;
      case 'movemodes_enabled':
        this.moveModesEnabled(data);
        break;
      case 'movemode_visible':
        this.moveModeVisibleAction(data);
        break;
      case 'movemode':
        this.moveModeAction(data);
        break;
    }
  };

  addOptions() {
    this.options.forEach((option) => {
      switch (option.name) {
        case 'Time': {
          if (TimeControl.isConfigurationValid(option)) {
            this.timeControl = new TimeControl(this.topControlsWrapper, option, this.ifwin);
            this.timeControl.render();
          }
          break;
        }
        case 'Floor': {
          if (FloorControl.isConfigurationValid(option)) {
            this.floorControl = new FloorControl(this.controlsContainer, option, this.ifwin);
            this.floorControl.render();
          }
          break;
        }
        default:
      }
    });
  }

  addActions() {
    const SendAction = (name, event) => {
      this.ifwin?.postMessage({ type: 'buttons', action: 'action', name, event }, '*');
    };

    this.actions.forEach((action) => {
      let control;

      switch (action.name) {
        case 'Help': {
          control = new HelpControl(this.controlsContainer);
          control.render(action, SendAction);
          break;
        }
        case 'Screenshot': {
          control = new ScreenshotControl(this.bottomControlsWrapper);
          control.render(action, SendAction);
          break;
        }
        default:
      }
    });
  }

  addMoveModes(data) {
    const moveModes = data.movemodes.filter((mm) => mm.name !== 'AR'); /* temporary fix */
    const isConfigurationValid = MovemodesControl.isConfigurationValid({
      current: data.currentmovemode,
      variants: moveModes.map((variant) => variant.name),
    });

    if (isConfigurationValid) {
      this.movemodesControl = new MovemodesControl(
        this.topControlsWrapper,
        this.ifwin,
        moveModes,
        data.currentmovemode,
      );
      this.movemodesControl.render();
    }
  }

  addFullscreen() {
    const fullscreenControl = new FullscreenControl(this.node, this.bottomControlsWrapper, {
      onFullscreen: this.config.onFullscreen,
      offFullscreen: this.config.offFullscreen,
    });
    fullscreenControl.render();
  }

  initAction(data) {
    // Ініціалізація
    // відмічаємо, що відбулась ініціалізація і більше не треба бомбити переглядач повідомленнями
    this.initdone = true;
    this.options = data.options; // options that allows to choose from several options
    this.actions = data.actions; // actions for which buttons can be provided in the interface

    // Відправляємо розмір логотипу VRnet
    this.ifwin?.postMessage(
      {
        type: 'buttons',
        action: 'logo',
        fullsize: false,
        position: 'bottomleft',
        borderX: 10 + 46 + 10,
        borderY: 10,
      },
      '*',
    );

    this.addOptions();
    this.addActions();
    this.addMoveModes(data);
    this.addFullscreen();
  }

  visibilityAction(data) {
    // Задання видимості панелі кнопок
    this.controlsContainer.style.display = data.visible ? 'block' : 'none';
  }

  optionVisibleAction(data) {
    // Задання видимості опції
    if (!this.options) return;
    const option = this.options.find((o) => o.name === data.name);
    if (!option) {
      throw new Error(`Option with name "${data.name}" not found`);
    }
    option.visible = data.visible;
    if (option.element) {
      if (option.visible) {
        option.element.classList.remove('hidden');
      } else {
        option.element.classList.add('hidden');
      }
    }
  }

  optionAction(data) {
    // Задання значення опції
    if (!this.options) return;
    const option = this.options.find((o) => o.name === data.name);
    if (!option) {
      throw new Error(`Option with name "${data.name}" not found`);
    }
    const value = option.variants.find((v) => v.value === data.value);
    if (!value) {
      throw new Error(`Value "${data.value}" not found in option ${data.name}`);
    }
    value.activate();
  }

  logoInfoAction() {
    // отримуємо нові розміри та положення логотипу
    const logoInfo = new LogoInfo(this.controlsContainer);
    logoInfo.render();
  }

  moveModesEnabled(data) {
    // Задання доступності режимів пересування для їх зміни кліком
    this.movemodesControl?.updateMovemodeEnabled(data.enabled);
  }

  moveModeVisibleAction(data) {
    // Задання видимості режимів пересування
    const moveMode = this.currentMoveMode(data);
    if (!moveMode) return;
    moveMode.visible = data.visible;
    if (!moveMode.visible) {
      moveMode.element.classList.add('hidden');
    }
  }

  moveModeAction(data) {
    // Задання нового режиму пересування
    const moveMode = this.currentMoveMode(data);
    if (!moveMode) return;
    moveMode.activate();
  }

  currentMoveMode(data) {
    return this.movemodesControl?.controlSetup?.find((moveMode) => moveMode.name === data.name);
  }

  unmountControls = () => {
    // видалення лістенера для показу контролів у 3D плануванні
    if (!this.onMessage) return;
    window.removeEventListener('message', this.onMessage, false);

    clearInterval(this.timer);
  };
}

export default PlanVr;
